import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth/auth.service';
import { GlobalStateService } from '../../../services/global/global-state.service';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LoginComponent extends DestructorComponent {
  loginForm: FormGroup;
  isLoading: boolean = false; // Loading state for button
  errorMessage: string = ''; // Error message display
  activeTab: 'email' | 'loginId' = 'email'; // Active tab state

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private globalStateService: GlobalStateService,
    private router: Router
  ) {
    super();
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      login_id: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    // Initialization logic can go here
    console.log('Login component initialized');
    // Set initial validators based on active tab
    this.updateValidators();
  }

  /**
   * Switch between email and login ID tabs
   */
  switchTab(tab: 'email' | 'loginId'): void {
    this.activeTab = tab;
    this.errorMessage = ''; // Clear error message on tab switch
    this.updateValidators();
  }

  /**
   * Update form validators based on active tab
   */
  private updateValidators(): void {
    const emailControl = this.loginForm.get('email');
    const loginIdControl = this.loginForm.get('login_id');

    if (this.activeTab === 'email') {
      // Enable email validation, disable login_id validation
      emailControl?.setValidators([Validators.required, Validators.email]);
      loginIdControl?.clearValidators();
      loginIdControl?.setValue('');
    } else {
      // Enable login_id validation, disable email validation
      loginIdControl?.setValidators([Validators.required]);
      emailControl?.clearValidators();
      emailControl?.setValue('');
    }

    // Update validity
    emailControl?.updateValueAndValidity();
    loginIdControl?.updateValueAndValidity();
  }

  /**
   * Handle form submission for user login
   */
  onSubmit(): void {
    // Clear previous error message
    this.errorMessage = '';

    // Check if form is valid
    if (this.loginForm.valid && !this.isLoading) {
      // Set loading state to prevent multiple API calls
      this.isLoading = true;

      const { email, login_id, password } = this.loginForm.value;

      // Prepare login data based on active tab
      const loginData: any = { password };

      if (this.activeTab === 'email') {
        loginData.email = email;
      } else {
        loginData.login_id = login_id;
      }

      // Call auth service login method
      this.subscriptions.push(
        this.authService.Login(loginData).subscribe({
          next: (response: any) => {
            // Handle successful login
            if (response && response.data && response.data.token) {
              // Use the new SetLoginState method to handle token-only storage
              this.globalStateService.SetLoginState(
                response.data.user || response.data,
                response.data.token
              );

              // Navigate to dashboard
              this.router.navigate(['/dashboard']);
            } else {
              // Handle case where token is not in expected format
              this.errorMessage = 'Invalid response format';
              this.isLoading = false;
            }
          },
          error: (error: any) => {
            // Handle login error
            console.error('Login error:', error);
            this.errorMessage = error.error?.message || 'Invalid credentials';
            this.isLoading = false;
          }
        })
      )

    } else {
      // Mark all fields as touched to show validation errors
      this.loginForm.markAllAsTouched();
    }
  }

  ngAfterViewInit(): void {
  }
}
